import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import {
  X,
  Loader2,
  Sparkles,
  User,
  CalendarClock,
  AlertTriangle,
  Zap,
} from 'lucide-react';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { autoScheduleAPI, serviceCallsAPI, usersAPI } from '@shared/apiClient';
import { useInvalidateDispatch } from '../../hooks/useDragAssign';

const MODES = [
  { id: 'system', label: 'System decides', icon: Sparkles },
  { id: 'technician', label: 'Pick a technician', icon: User },
  { id: 'manual', label: 'Manual', icon: CalendarClock },
];

const todayStr = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return toDateInputValue(d);
};

function toDateInputValue(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

const addDays = (dateStr, days) => {
  const [y, m, d] = dateStr.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  date.setDate(date.getDate() + days);
  return toDateInputValue(date);
};

const formatDayLabel = (dateStr) => {
  if (!dateStr) return '—';
  const [y, m, d] = dateStr.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  return date.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
};

const fmtMin = (mins) => {
  if (mins == null) return '—';
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
};

function flattenReconfigurePreview(days) {
  const assignments = [];
  days.forEach((day) => {
    day.techSlots.forEach((slot) => {
      slot.assignments.forEach((unit) => {
        unit.members.forEach((member) => {
          assignments.push({
            serviceCallId: member.serviceCallId,
            technicianId: slot.technicianId,
            technicianName: slot.technicianName,
            scheduledDate: slot.date,
            typeId: member.typeId,
            displayName: member.displayName,
            originalDate: member.originalDate,
            moved: Boolean(member.originalDate) && member.originalDate !== slot.date,
          });
        });
      });
    });
  });
  return assignments;
}

function CandidateList({ title, candidates, selectedKey, onSelect, emptyMessage }) {
  return (
    <div className="rounded-lg border border-gray-200 overflow-hidden">
      <div className="px-3 py-2 bg-gray-50 border-b border-gray-100 text-xs font-semibold text-gray-700">
        {title}
      </div>
      <div className="p-2 space-y-1.5 max-h-64 overflow-auto">
        {candidates.length === 0 ? (
          <div className="text-xs text-gray-500 px-1 py-2">{emptyMessage}</div>
        ) : (
          candidates.map((candidate) => {
            const key = `${candidate.technicianId}|${candidate.date}`;
            const selected = selectedKey === key;
            return (
              <label
                key={key}
                className={clsx(
                  'flex items-start gap-2 rounded-md border px-2.5 py-2 cursor-pointer text-xs',
                  selected ? 'border-primary-400 bg-primary-50' : 'border-gray-200 hover:bg-gray-50'
                )}
              >
                <input
                  type="radio"
                  checked={selected}
                  onChange={() => onSelect(candidate)}
                  className="mt-0.5"
                />
                <span>
                  <span className="font-medium text-gray-900">{candidate.technicianName}</span>
                  {' · '}
                  {formatDayLabel(candidate.date)}
                  {' · '}
                  +{fmtMin(candidate.addedMinutes)} drive
                  {' · '}
                  {fmtMin(candidate.projectedDayTotalMinutes)} / {fmtMin(candidate.dayBudgetMinutes)} day
                </span>
              </label>
            );
          })
        )}
      </div>
    </div>
  );
}

export default function ScheduleRepairModal({ serviceCallId, isOpen, onClose }) {
  const invalidate = useInvalidateDispatch();
  const [mode, setMode] = useState('system');

  // System decides
  const [windowStart, setWindowStart] = useState('');
  const [windowEnd, setWindowEnd] = useState('');
  const [emergency, setEmergency] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);

  // Pick a technician
  const [techId, setTechId] = useState('');
  const [techStart, setTechStart] = useState(todayStr());
  const [techEnd, setTechEnd] = useState(addDays(todayStr(), 13));
  const [reconfigurePreview, setReconfigurePreview] = useState(null);

  // Manual
  const [manualTechId, setManualTechId] = useState('');
  const [manualDate, setManualDate] = useState('');

  useEffect(() => {
    if (!isOpen) {
      setMode('system');
      setWindowStart('');
      setWindowEnd('');
      setEmergency(false);
      setSelectedCandidate(null);
      setTechId('');
      setTechStart(todayStr());
      setTechEnd(addDays(todayStr(), 13));
      setReconfigurePreview(null);
      setManualTechId('');
      setManualDate('');
    }
  }, [isOpen]);

  const placementsQuery = useQuery(
    ['repairPlacements', serviceCallId, windowStart, windowEnd, emergency],
    async () => {
      const params = { serviceCallId };
      if (emergency) {
        params.windowStart = todayStr();
        params.windowEnd = todayStr();
      } else {
        if (windowStart) params.windowStart = windowStart;
        if (windowEnd) params.windowEnd = windowEnd;
      }
      const response = await autoScheduleAPI.getRepairPlacements(params);
      return response.data;
    },
    { enabled: isOpen && !!serviceCallId && mode === 'system' }
  );

  const techniciansQuery = useQuery(
    ['techniciansForRepairSchedule'],
    () => usersAPI.getUsers({ role: 'technician', limit: 100 }),
    { enabled: isOpen }
  );
  const technicians = (techniciansQuery.data?.data?.users || []).filter((t) => t.isActive);

  const scheduleMutation = useMutation(
    ({ technicianId, scheduledDate }) => serviceCallsAPI.scheduleServiceCall(serviceCallId, {
      technicianId,
      scheduledDate,
    }),
    {
      onSuccess: () => {
        toast.success('Repair scheduled');
        invalidate();
        onClose();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to schedule repair');
      },
    }
  );

  const previewReconfigureMutation = useMutation(
    () => autoScheduleAPI.preview({
      startDate: techStart,
      endDate: techEnd,
      technicianIds: [techId],
      includeScheduled: true,
      explicitCallIds: [serviceCallId],
      strategy: 'minimize_travel',
    }),
    {
      onSuccess: (response) => {
        setReconfigurePreview(response.data.preview);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Preview failed');
      },
    }
  );

  const applyReconfigureMutation = useMutation(
    (assignments) => autoScheduleAPI.apply({ assignments }),
    {
      onSuccess: (response) => {
        const { applied, failed } = response.data;
        toast.success(`Scheduled ${applied} visit(s)${failed ? `, ${failed} failed` : ''}`);
        invalidate();
        onClose();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Apply failed');
      },
    }
  );

  const manualPreviewQuery = useQuery(
    ['manualRepairSlotPreview', serviceCallId, manualTechId, manualDate],
    async () => {
      const response = await autoScheduleAPI.recomputeSlot({
        date: manualDate,
        technicianId: manualTechId,
        serviceCallIds: [serviceCallId],
      });
      return response.data.slot;
    },
    { enabled: isOpen && mode === 'manual' && !!manualTechId && !!manualDate }
  );

  const reconfigureAssignments = useMemo(() => {
    if (!reconfigurePreview?.days) return [];
    return flattenReconfigurePreview(reconfigurePreview.days);
  }, [reconfigurePreview]);

  const repairUnplaced = reconfigurePreview?.unplaceable?.some(
    (u) => u.members?.some((m) => m.serviceCallId === serviceCallId)
  );

  if (!isOpen) return null;

  const placements = placementsQuery.data;

  return (
    <div className="fixed inset-0 z-[60] overflow-y-auto">
      <div className="flex min-h-full items-end sm:items-center justify-center p-4">
        <div className="fixed inset-0 bg-gray-900/50" onClick={onClose} />
        <div className="relative w-full max-w-2xl bg-white rounded-lg shadow-xl max-h-[90vh] flex flex-col">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
            <div>
              <h2 className="text-base font-semibold text-gray-900">Schedule Repair</h2>
              {placements?.displayName && (
                <p className="text-xs text-gray-500">
                  {placements.displayName}
                  {placements.callKind ? ` · ${placements.callKind}` : ''}
                  {placements.onSiteMinutes ? ` · ${fmtMin(placements.onSiteMinutes)} on-site` : ''}
                </p>
              )}
            </div>
            <button type="button" onClick={onClose} className="p-1 rounded hover:bg-gray-100 text-gray-500">
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="px-4 pt-3 flex gap-1.5 border-b border-gray-200">
            {MODES.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                type="button"
                onClick={() => setMode(id)}
                className={clsx(
                  'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-t-md border border-b-0',
                  mode === id
                    ? 'border-gray-200 bg-white text-primary-700'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                )}
              >
                <Icon className="h-3.5 w-3.5" />
                {label}
              </button>
            ))}
          </div>

          <div className="flex-1 overflow-auto p-4 space-y-4">
            {mode === 'system' && (
              <>
                <div className="flex flex-wrap items-end gap-2">
                  <div>
                    <label className="block text-2xs font-medium text-gray-600 mb-1">From (optional)</label>
                    <input
                      type="date"
                      value={windowStart}
                      disabled={emergency}
                      onChange={(e) => setWindowStart(e.target.value)}
                      className="border border-gray-300 rounded-md px-2 py-1 text-xs disabled:bg-gray-100"
                    />
                  </div>
                  <div>
                    <label className="block text-2xs font-medium text-gray-600 mb-1">To (optional)</label>
                    <input
                      type="date"
                      value={windowEnd}
                      disabled={emergency}
                      onChange={(e) => setWindowEnd(e.target.value)}
                      className="border border-gray-300 rounded-md px-2 py-1 text-xs disabled:bg-gray-100"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => setEmergency((v) => !v)}
                    className={clsx(
                      'inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium border',
                      emergency
                        ? 'border-red-300 bg-red-50 text-red-800'
                        : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                    )}
                    title="Restrict to today only, for urgent repairs"
                  >
                    <Zap className="h-3.5 w-3.5" />
                    Emergency (today)
                  </button>
                </div>

                {placementsQuery.isLoading ? (
                  <div className="flex items-center justify-center py-12 text-sm text-gray-500 gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Finding placement options...
                  </div>
                ) : placements?.reason === 'needs_geocoding' ? (
                  <div className="text-sm text-amber-700 py-4">
                    This client needs to be geocoded before placement options can be calculated.
                  </div>
                ) : (
                  <div className="grid sm:grid-cols-2 gap-3">
                    <CandidateList
                      title="Soonest available"
                      candidates={placements?.soonest || []}
                      selectedKey={selectedCandidate ? `${selectedCandidate.technicianId}|${selectedCandidate.date}` : null}
                      onSelect={setSelectedCandidate}
                      emptyMessage="No feasible slot in this window."
                    />
                    <CandidateList
                      title="Least driving"
                      candidates={placements?.leastDrive || []}
                      selectedKey={selectedCandidate ? `${selectedCandidate.technicianId}|${selectedCandidate.date}` : null}
                      onSelect={setSelectedCandidate}
                      emptyMessage="No feasible slot in this window."
                    />
                  </div>
                )}
              </>
            )}

            {mode === 'technician' && (
              <>
                <div className="flex flex-wrap items-end gap-2">
                  <div className="flex-1 min-w-[160px]">
                    <label className="block text-2xs font-medium text-gray-600 mb-1">Technician</label>
                    <select
                      value={techId}
                      onChange={(e) => { setTechId(e.target.value); setReconfigurePreview(null); }}
                      className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-xs"
                    >
                      <option value="">Select a technician</option>
                      {technicians.map((tech) => (
                        <option key={tech.id} value={tech.id}>
                          {tech.firstName} {tech.lastName}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-2xs font-medium text-gray-600 mb-1">From</label>
                    <input
                      type="date"
                      value={techStart}
                      onChange={(e) => { setTechStart(e.target.value); setReconfigurePreview(null); }}
                      className="border border-gray-300 rounded-md px-2 py-1 text-xs"
                    />
                  </div>
                  <div>
                    <label className="block text-2xs font-medium text-gray-600 mb-1">To</label>
                    <input
                      type="date"
                      value={techEnd}
                      onChange={(e) => { setTechEnd(e.target.value); setReconfigurePreview(null); }}
                      className="border border-gray-300 rounded-md px-2 py-1 text-xs"
                    />
                  </div>
                  <button
                    type="button"
                    disabled={!techId || previewReconfigureMutation.isLoading}
                    onClick={() => previewReconfigureMutation.mutate()}
                    className="px-3 py-1.5 text-xs font-medium rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
                  >
                    {previewReconfigureMutation.isLoading ? 'Previewing...' : 'Preview'}
                  </button>
                </div>

                <p className="text-xs text-gray-500">
                  The system will fit the repair into this technician&apos;s schedule, reshuffling their
                  other unlocked visits within the date range if it helps.
                </p>

                {reconfigurePreview && (
                  <div className="space-y-3">
                    {repairUnplaced && (
                      <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                        The repair could not be placed in this window for this technician. Try a wider
                        date range or a different technician.
                      </div>
                    )}
                    <div className="flex items-center gap-3 text-xs text-gray-600">
                      <span><strong>{reconfigurePreview.summary?.movedCalls ?? 0}</strong> moved</span>
                      <span><strong>{reconfigurePreview.summary?.unchangedCalls ?? 0}</strong> unchanged</span>
                      <span><strong>{fmtMin(reconfigurePreview.summary?.totalDriveMinutes)}</strong> total drive</span>
                    </div>
                    <div className="rounded-lg border border-gray-200 divide-y divide-gray-100">
                      {reconfigureAssignments.map((assignment) => (
                        <div
                          key={assignment.serviceCallId}
                          className={clsx(
                            'px-3 py-2 flex items-center justify-between gap-3 text-xs',
                            assignment.serviceCallId === serviceCallId && 'bg-primary-50/60'
                          )}
                        >
                          <div>
                            <div className="font-medium text-gray-900">
                              {assignment.displayName}
                              {assignment.serviceCallId === serviceCallId && (
                                <span className="ml-1.5 text-2xs px-1 rounded bg-primary-100 text-primary-800">
                                  this repair
                                </span>
                              )}
                            </div>
                            <div className="text-gray-500">
                              {assignment.moved && (
                                <>From {formatDayLabel(assignment.originalDate)} → </>
                              )}
                              {formatDayLabel(assignment.scheduledDate)}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}

            {mode === 'manual' && (
              <>
                <div className="flex flex-wrap items-end gap-2">
                  <div className="flex-1 min-w-[160px]">
                    <label className="block text-2xs font-medium text-gray-600 mb-1">Technician</label>
                    <select
                      value={manualTechId}
                      onChange={(e) => setManualTechId(e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-xs"
                    >
                      <option value="">Select a technician</option>
                      {technicians.map((tech) => (
                        <option key={tech.id} value={tech.id}>
                          {tech.firstName} {tech.lastName}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-2xs font-medium text-gray-600 mb-1">Date</label>
                    <input
                      type="date"
                      value={manualDate}
                      onChange={(e) => setManualDate(e.target.value)}
                      className="border border-gray-300 rounded-md px-2 py-1 text-xs"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => setManualDate(todayStr())}
                    className="px-2.5 py-1.5 text-xs font-medium rounded-md border border-gray-300 text-gray-600 hover:bg-gray-50"
                  >
                    Today
                  </button>
                </div>

                {manualPreviewQuery.isLoading && (
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Checking this slot...
                  </div>
                )}

                {manualPreviewQuery.data && (
                  <div className={clsx(
                    'rounded-lg border px-3 py-2 text-xs',
                    manualPreviewQuery.data.overBudget
                      || manualPreviewQuery.data.incompatible
                      || manualPreviewQuery.data.techRuleViolation
                      ? 'border-amber-300 bg-amber-50 text-amber-900'
                      : 'border-gray-200 bg-gray-50 text-gray-700'
                  )}>
                    <div className="flex items-center gap-2 font-medium">
                      {(manualPreviewQuery.data.overBudget
                        || manualPreviewQuery.data.incompatible
                        || manualPreviewQuery.data.techRuleViolation) && (
                        <AlertTriangle className="h-3.5 w-3.5" />
                      )}
                      {fmtMin(manualPreviewQuery.data.usedMinutes)} / {fmtMin(manualPreviewQuery.data.budgetMinutes)} for the day
                      {manualPreviewQuery.data.overBudget && ' (over capacity)'}
                    </div>
                    {manualPreviewQuery.data.incompatible && (
                      <div className="mt-1">This technician may not have the required capability for one or more stops.</div>
                    )}
                    {manualPreviewQuery.data.techRuleViolation && (
                      <div className="mt-1">
                        This customer&apos;s technician rules exclude this tech. You can still schedule it as an override.
                      </div>
                    )}
                    <div className="mt-1 text-2xs text-gray-500">
                      {fmtMin(manualPreviewQuery.data.driveMinutes)} drive time for the day
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1.5 text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>

            {mode === 'system' && (
              <button
                type="button"
                disabled={!selectedCandidate || scheduleMutation.isLoading}
                onClick={() => scheduleMutation.mutate({
                  technicianId: selectedCandidate.technicianId,
                  scheduledDate: selectedCandidate.date,
                })}
                className="px-3 py-1.5 text-sm rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
              >
                {scheduleMutation.isLoading ? 'Scheduling...' : 'Schedule'}
              </button>
            )}

            {mode === 'technician' && (
              <button
                type="button"
                disabled={reconfigureAssignments.length === 0 || applyReconfigureMutation.isLoading}
                onClick={() => applyReconfigureMutation.mutate(
                  reconfigureAssignments.map(({ serviceCallId: id, technicianId, scheduledDate, typeId }) => ({
                    serviceCallId: id,
                    technicianId,
                    scheduledDate,
                    typeId,
                  }))
                )}
                className="px-3 py-1.5 text-sm rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
              >
                {applyReconfigureMutation.isLoading ? 'Applying...' : `Apply ${reconfigureAssignments.length} placement(s)`}
              </button>
            )}

            {mode === 'manual' && (
              <button
                type="button"
                disabled={!manualTechId || !manualDate || scheduleMutation.isLoading}
                onClick={() => scheduleMutation.mutate({
                  technicianId: manualTechId,
                  scheduledDate: manualDate,
                })}
                className="px-3 py-1.5 text-sm rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
              >
                {scheduleMutation.isLoading ? 'Scheduling...' : 'Schedule'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
